import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Package, Truck, Home, Search, XCircle } from "lucide-react";
import { api, inr } from "../lib/api";

const STEPS = [
  { id: "confirmed", label: "Confirmed", icon: Check },
  { id: "packed", label: "Packed", icon: Package },
  { id: "shipped", label: "Shipped", icon: Truck },
  { id: "delivered", label: "Delivered", icon: Home },
];

const Track = () => {
  const location = useLocation();
  const [form, setForm] = useState({
    order_number: location.state?.order_number || "",
    email: location.state?.email || "",
  });
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/track", {
        order_number: form.order_number.trim().toUpperCase(),
        email: form.email.trim(),
      });
      setOrder(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setOrder(null);
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const cancelled = order?.order_status === "cancelled";
  const statusIdx = { confirmed: 0, shipped: 2, delivered: 3 }[order?.order_status] ?? 0;

  return (
    <main className="pt-[68px] min-h-screen" data-testid="track-page">
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-14 md:py-20">
        <div className="text-center">
          <p className="eyebrow mb-3">Order Tracking</p>
          <h1 className="font-serif font-light text-4xl md:text-5xl text-zinc-100">
            Where's my <span className="italic text-gold-gradient">sparkle</span>?
          </h1>
          <p className="mt-4 text-zinc-500 text-sm max-w-md mx-auto">
            Enter the order number from your confirmation (it starts with AUR-) along with the email you used.
          </p>
        </div>

        <form
          onSubmit={submit}
          className="mt-10 rounded-2xl gold-hairline bg-[#101013] p-6 md:p-8 grid sm:grid-cols-2 gap-5"
          data-testid="track-form"
        >
          <div>
            <label className="text-xs tracking-[0.14em] uppercase text-zinc-500 block mb-2">Order Number</label>
            <input
              required
              value={form.order_number}
              onChange={(e) => setForm((f) => ({ ...f, order_number: e.target.value.toUpperCase() }))}
              placeholder="AUR-XXXXXXXX"
              className="input-dark"
              data-testid="track-order-input"
            />
          </div>
          <div>
            <label className="text-xs tracking-[0.14em] uppercase text-zinc-500 block mb-2">Email</label>
            <input
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="you@example.com"
              className="input-dark"
              data-testid="track-email-input"
            />
          </div>
          <div className="sm:col-span-2">
            <button type="submit" disabled={busy} className="btn-gold w-full" data-testid="track-submit-button">
              <Search size={16} /> {busy ? "Looking up..." : "Track My Order"}
            </button>
          </div>
        </form>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 rounded-xl border border-red-500/30 bg-red-500/10 px-5 py-4 text-sm text-red-300"
            data-testid="track-error"
          >
            {error}
          </motion.div>
        )}

        {order && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 space-y-6"
            data-testid="track-result"
          >
            <div className="rounded-2xl border border-[#d4af37]/25 bg-[#101013] p-6 md:p-8">
              <div className="flex flex-wrap justify-between items-start gap-3 mb-8">
                <div>
                  <p className="text-xs tracking-[0.18em] uppercase text-zinc-500">Order</p>
                  <p className="font-serif text-2xl text-[#f3e5ab]" data-testid="track-order-number">{order.order_number}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs tracking-[0.18em] uppercase text-zinc-500">Placed</p>
                  <p className="text-sm text-zinc-300 mt-1">
                    {new Date(order.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
                  </p>
                </div>
              </div>

              {cancelled ? (
                <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-5 py-4 flex items-start gap-3" data-testid="track-cancelled">
                  <XCircle size={18} className="text-red-400 mt-0.5 shrink-0" />
                  <p className="text-sm text-red-300">
                    This order was cancelled. If you were expecting a refund, it reflects in 5-7 business days.
                  </p>
                </div>
              ) : (
                <div className="flex items-center justify-between" data-testid="track-timeline">
                  {STEPS.map((s, i) => (
                    <div key={s.id} className="flex flex-col items-center relative flex-1">
                      {i < STEPS.length - 1 && (
                        <div className={`absolute top-4 left-1/2 w-full h-px ${i < statusIdx ? "bg-[#d4af37]" : "bg-[#d4af37]/20"}`} />
                      )}
                      <motion.span
                        initial={{ scale: 0.6, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.15 + i * 0.12, type: "spring", stiffness: 220, damping: 16 }}
                        className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center border ${
                          i <= statusIdx ? "bg-[#d4af37] text-[#0a0a0b] border-[#d4af37]" : "bg-[#0c0c0e] text-zinc-600 border-[#d4af37]/25"
                        }`}
                      >
                        <s.icon size={14} strokeWidth={2} />
                      </motion.span>
                      <span className={`mt-2 text-[10px] tracking-[0.14em] uppercase ${i <= statusIdx ? "text-[#f3e5ab]" : "text-zinc-600"}`}>
                        {s.label}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {(order.shiprocket?.awb || order.shiprocket?.courier_name) && (
              <div className="rounded-2xl border border-[#d4af37]/25 bg-[#101013] p-6 md:p-8 flex flex-wrap items-center justify-between gap-4" data-testid="track-shipment">
                <div>
                  <p className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-1">Shipment</p>
                  <p className="text-sm text-zinc-300" data-testid="track-courier-info">
                    {order.shiprocket.courier_name || "Courier assigned"}
                    {order.shiprocket.awb ? (
                      <> · AWB <span className="text-[#f3e5ab] font-medium">{order.shiprocket.awb}</span></>
                    ) : null}
                  </p>
                </div>
                <div className="flex gap-3">
                  {order.shiprocket.awb && (
                    <a
                      href={`https://shiprocket.co/tracking/${order.shiprocket.awb}`}
                      target="_blank"
                      rel="noreferrer"
                      className="btn-ghost !px-5 !py-2.5"
                      data-testid="track-live-link"
                    >
                      Track Live
                    </a>
                  )}
                </div>
              </div>
            )}

            <div className="rounded-2xl border border-[#d4af37]/25 bg-[#101013] p-6 md:p-8">
              <h2 className="font-serif text-2xl text-zinc-100 mb-6">Your Pieces</h2>              <div className="space-y-4">
                {order.items.map((i) => (
                  <div key={i.product_id} className="flex gap-4 items-center" data-testid={`track-item-${i.product_id}`}>
                    <img src={i.image} alt={i.name} className="w-16 h-20 object-cover rounded-lg border border-[#d4af37]/15" />
                    <div className="flex-1">
                      <p className="font-serif text-lg text-zinc-100">{i.name}</p>
                      <p className="text-xs text-zinc-500 mt-0.5">Qty {i.qty}</p>
                    </div>
                    <p className="text-sm text-zinc-300">{inr(i.price * i.qty)}</p>
                  </div>
                ))}
              </div>

              <div className="mt-6 pt-5 border-t border-[#d4af37]/15 grid sm:grid-cols-2 gap-6">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-zinc-400"><span>Subtotal</span><span>{inr(order.subtotal)}</span></div>
                  {order.discount > 0 && <div className="flex justify-between text-emerald-400"><span>Discount</span><span>−{inr(order.discount)}</span></div>}
                  <div className="flex justify-between text-zinc-400"><span>Shipping</span><span>{order.shipping === 0 ? "Free" : inr(order.shipping)}</span></div>
                  <div className="flex justify-between text-zinc-100 font-semibold pt-2 border-t border-[#d4af37]/15" data-testid="track-total">
                    <span>Total {order.payment_status === "paid" ? "Paid" : "Due"}</span><span>{inr(order.total)}</span>
                  </div>
                </div>
                <div className="text-sm text-zinc-400">
                  <p className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2">Delivering To</p>
                  <p className="text-zinc-300">{order.customer.name}</p>
                  <p className="mt-1">{order.address.line1}, {order.address.city}, {order.address.state} — {order.address.pincode}</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        <p className="mt-10 text-center text-sm text-zinc-500">
          Need help? Reach us on Instagram or email — quote your order number.
        </p>
      </div>
    </main>
  );
};

export default Track;
